import { useCallback, useRef, useState } from 'react';
import {
  getReportCreatorsAction,
  getReportSelectionPreferences,
  getReportsAction,
  newReportAction,
  saveReportSelectionPreferences,
} from 'contexts/reporting/actions';
import { reportsInitialParams } from 'contexts/reporting/utils';
import {
  IGetReportsActionParams,
  INewReportParams,
  IReportCreator,
  IReportsDataCtx,
  IUserFilter,
} from 'contexts/reporting/types';
import Notification from 'components/notification';
import { getInvitations } from 'services/users';
import { filtersByTab } from 'pages/Reporting/components/ReportingFiltersConfig';
import { MAX_LICENSES_ACCOUNT } from 'constants/licenses';

const useReportingContent = (): IReportsDataCtx => {
  const [reports, setReports] = useState<IReportsDataCtx['reports'] | null>(null);
  const [activeFilters, setActiveFilters] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [fetchingFilters, setFetchingFilters] = useState(false);
  const [reportCreators, setReportCreators] = useState<IReportCreator[]>([]);
  const [fetchingReportCreators, setFetchingReportCreators] = useState(false);
  const [users, setUsers] = useState<Array<IUserFilter>>([]);
  const lastReportsParams = useRef<IGetReportsActionParams | null>(null);
  const usersRef = useRef(users);

  usersRef.current = users;

  const getReports = async (reportParams: IGetReportsActionParams = {}, fetch?: boolean) => {
    const { filtersType, ordering, page, page_size, created_by_ids } = reportParams;

    lastReportsParams.current = {
      ...reportParams,
      created_by_ids: created_by_ids ? [...created_by_ids] : undefined,
    };

    setFetching(fetch ? true : false);
    try {
      const params = {
        ...reportsInitialParams,
        type: filtersType,
        page,
        page_size,
        ordering,
        created_by_ids: created_by_ids?.length ? created_by_ids.join(',') : undefined,
        from_date: reportsInitialParams.from_date || undefined,
        to_date: reportsInitialParams.to_date || undefined,
      };

      const reportsResponse = await getReportsAction(params);

      setReports(reportsResponse);
    } catch (error) {
      Notification({ text: 'There has been a problem fetching your reports', type: 'error' });
    } finally {
      setFetching(false);
    }
  };

  const refreshReports = async () => {
    if (lastReportsParams.current) {
      await getReports(lastReportsParams.current, true);
    } else {
      setFetching(false);
    }
  };

  const generateNewReport = async (params: INewReportParams) => {
    setFetching(true);
    try {
      await saveReportsFiltersPreferences(params.type, activeFilters);
      const newReportMethod = newReportAction;
      const newReportResponse = await newReportMethod(params);

      Notification({
        text: 'Report generated successfully! Find it in Report History',
        type: 'success',
      });

      setReports((prevReports) => {
        const previousResults = prevReports?.results ?? [];
        const previousCount = prevReports?.count ?? 0;
        return {
          results: [newReportResponse.result, ...previousResults],
          count: previousCount + 1,
        };
      });

      await refreshReports();
    } catch (error) {
      Notification({ text: 'There has been a problem generating your report', type: 'error' });
      setFetching(false);
    }
  };

  const getReportsFilteringPreferences = async (reportType: string) => {
    setFetchingFilters(true);
    try {
      const preferences = await getReportSelectionPreferences();
      const currentActiveFilters =
        preferences?.results
          .find((p) => p.type === reportType)
          .checkboxes?.filter((c) => filtersByTab[reportType].includes(c)) || [];
      setActiveFilters(currentActiveFilters);
    } catch (error) {
      Notification({ text: 'There has been a problem getting your preferences', type: 'error' });
    } finally {
      setFetchingFilters(false);
    }
  };

  const saveReportsFiltersPreferences = async (reportType: string, filters?: Array<string>) => {
    const preferences = filters ? filters : activeFilters;
    await saveReportSelectionPreferences(preferences, reportType);
  };

  const getUsers = useCallback(async (query = ''): Promise<IUserFilter[]> => {
    setFetchingUsers(true);
    try {
      const response = await getInvitations({
        active: 'true',
        license: null,
        teams: null,
        roles: null,
        page: 1,
        page_size: MAX_LICENSES_ACCOUNT,
        search: query,
      });

      if (response.status === 200) {
        const newUsers = response.data.results.map((u) => {
          return {
            email: u.email,
            id: u.uaa_id,
            name: `${u.uaa_data?.profile?.data?.first_name} ${u.uaa_data?.profile?.data?.last_name}`,
            business_id: u.user,
          };
        });
        usersRef.current = newUsers;
        setUsers(newUsers);
        return newUsers;
      }

      return usersRef.current;
    } catch (error) {
      Notification({ text: 'There has been a problem fetching users', type: 'error' });
      return usersRef.current;
    } finally {
      setFetchingUsers(false);
    }
  }, []);

  const getReportCreators = async (): Promise<void> => {
    setFetchingReportCreators(true);
    try {
      const reportCreatorsData = await getReportCreatorsAction();
      setReportCreators(reportCreatorsData);
    } catch (error) {
      Notification({ text: 'There has been a problem fetching report creators', type: 'error' });
    } finally {
      setFetchingReportCreators(false);
    }
  };

  return {
    fetching,
    fetchingUsers,
    fetchingFilters,
    reports,
    activeFilters,
    setActiveFilters,
    users,
    getReports,
    refreshReports,
    getReportCreators,
    reportCreators,
    fetchingReportCreators,
    generateNewReport,
    getReportsFilteringPreferences,
    saveReportsFiltersPreferences,
    getUsers,
    error: false,
  };
};

export default useReportingContent;
